import { DabeeoMap } from 'dabeeomaps';
import type { IControl } from 'dabeeomaps/dist/src/model/common/IControl';
import type { IVector2 } from 'dabeeomaps/dist/src/model/common/IVector2';
import type { IFocusTo } from 'dabeeomaps/dist/src/model/map/IFocusTo';
import { useCallback, useEffect, useMemo, useRef } from 'react';

export const useMapControl = (newMap?: DabeeoMap) => {
    const moveToTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleControlMap = useCallback(
        (control: IControl) => {
            if (!newMap) return;

            newMap?.control?.set({
                transition: true,
                ...control,
            });
        },
        [newMap]
    );

    const handleMoveTo = useCallback(
        ({ transition = true, ...rest }: { position: IVector2; transition?: boolean; floorId?: string }) => {
            if (!newMap) return;

            if (moveToTimeoutRef.current) {
                clearTimeout(moveToTimeoutRef.current);
            }

            moveToTimeoutRef.current = setTimeout(() => {
                newMap.control?.moveTo({ transition, ...rest });
                moveToTimeoutRef.current = null;
            }, 50);
        },
        [newMap]
    );

    const handleChangeZoom = useCallback(
        ({ zoom, transition = true }: { zoom: number; transition?: boolean }) => {
            if (!newMap) return;
            newMap.control?.changeZoom?.({ zoom, transition });
        },
        [newMap]
    );

    const currentCameraInfo = useMemo(() => {
        if (!newMap) return;
        return newMap?.control?.getCurrentCameraInfo?.();
    }, [newMap]);

    const handleResetMap = useCallback(() => {
        if (!newMap) return;
        newMap.control?.reset?.();
    }, [newMap]);

    const handleFocusTo = useCallback(
        (options: IFocusTo) => {
            if (!newMap) return;

            newMap?.control?.focusTo?.(options);
        },
        [newMap]
    );

    useEffect(() => {
        return () => {
            if (moveToTimeoutRef.current) {
                clearTimeout(moveToTimeoutRef.current);
            }
        };
    }, [newMap]);

    return {
        handleControlMap,
        handleMoveTo,
        handleChangeZoom,
        handleResetMap,
        handleFocusTo,
        currentCameraInfo,
    };
};
